import { useAxiosStore } from '@risksmart-app/components/src/hooks/useAxios';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { getOrganization } from '@risksmart-app/components/src/utils/authUtils';
import type { ReportPayload, TableData } from '@risksmart-app/pdf-report';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { handleError } from '@/utils/errorUtils';
import { applyTokenFilter } from '@/utils/filters/tokenFilter';
import { useExportToPdf } from '@/utils/table/hooks/useExportToPdf';
import type {
  TableFields,
  TablePropsWithActions,
  TableRecord,
} from '@/utils/table/types';

interface UsePagePdfExportOptions<T extends TableRecord> {
  tableProps: TablePropsWithActions<T>;
  fields: TableFields<T>;
  ribbonFilters?: FilterModal[];
  pdfTemplateId?: string;
  entityLabel: string;
}

interface PdfGenerationResponse {
  success: boolean;
  downloadUrl?: string;
  filename?: string;
}

// Tweakable weight for the last column when computing PDF column width ratios.
// Increase (>1) to make the last column wider; decrease (<1) to make it narrower.
// Has no effect on the UI, only influences PDF export ratios.
const LAST_COLUMN_RATIO_WEIGHT = 2.5;

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

const UNRATED_VALUES = new Set([
  'unrated',
  'not rated',
  'n/a',
  'na',
  'none',
  '-',
]);

// The engine renders exactly what it is sent; display defaults like the grey
// "unrated" badge are applied here when building the payload.
const buildCellStyles = (
  rows: string[][],
  cellStyles:
    | (null | undefined | { backgroundColor?: string; color?: string })[][]
    | undefined
): TableData['cellStyles'] =>
  rows.map((row, rowIndex) =>
    row.map((cell, cellIndex) => {
      const style = cellStyles?.[rowIndex]?.[cellIndex];
      const backgroundColor =
        style?.backgroundColor && HEX_COLOUR.test(style.backgroundColor)
          ? style.backgroundColor
          : undefined;
      const color =
        style?.color && HEX_COLOUR.test(style.color) ? style.color : undefined;
      if (backgroundColor || color) {
        return { backgroundColor, color };
      }
      if (UNRATED_VALUES.has(cell.trim().toLowerCase())) {
        return { backgroundColor: '#E8E8EC', color: '#73738C' };
      }

      return null;
    })
  );

interface AppliedFilterToken {
  property: string;
  operator: string;
  value: string;
  operation?: 'and' | 'or';
}

const buildFiltersSummary = (
  filterInfo: string | undefined,
  appliedFilters: AppliedFilterToken[] | undefined
): string | undefined => {
  const parts: string[] = [];
  if (filterInfo) {
    parts.push(filterInfo);
  }
  if (appliedFilters && appliedFilters.length > 0) {
    const expression = appliedFilters
      .map(
        (token) =>
          `${token.property} ${token.operator} "${token.value}"${
            token.operation ? ` ${token.operation.toUpperCase()} ` : ''
          }`
      )
      .join('');
    parts.push(`Filters: ${expression.trim()}`);
  }

  return parts.length > 0 ? parts.join(' — ') : undefined;
};

const ensurePdfExtension = (name: string): string =>
  name.replace(/\.pdf$/i, '').concat('.pdf');

export function usePagePdfExport<T extends TableRecord>({
  tableProps,
  fields,
  ribbonFilters,
  pdfTemplateId = 'default-register',
  entityLabel,
}: UsePagePdfExportOptions<T>) {
  const { user } = useRisksmartUser();
  const orgName = getOrganization(user);
  // Derive visible columns from table preferences, falling back to all field keys
  const visibleColumns = useMemo(() => {
    // First try to get from table preferences
    const preferencesColumns =
      tableProps.preferenceDetails?.preferences?.contentDisplay
        ?.filter((c) => c.visible)
        .map((c) => c.id);

    if (preferencesColumns && preferencesColumns.length > 0) {
      return preferencesColumns;
    }

    // Fallback to visible columns from tableProps
    if (tableProps.visibleColumns && tableProps.visibleColumns.length > 0) {
      return tableProps.visibleColumns;
    }

    // Final fallback to all field keys
    return Object.keys(fields);
  }, [
    tableProps.preferenceDetails?.preferences?.contentDisplay,
    tableProps.visibleColumns,
    fields,
  ]);

  const { authorisedAxiosInstance: axios } = useAxiosStore();
  const { addNotification } = useNotifications();
  const { t } = useTranslation(['common']);

  const { exportToCsvString, generateTableData } = useExportToPdf({
    tableProps: {
      allItems: tableProps.allItems,
      // The full filtered + sorted set across all pages — NOT the paginated
      // `tableProps.items`, which limits the export to the current page (CUS-83).
      items: tableProps.allPageItems ?? tableProps.items,
      filteringProperties: tableProps.filteringProperties,
      propertyFilterQuery: tableProps.propertyFilterQuery,
      visibleColumns: visibleColumns,
      fields: fields,
      entityLabel: entityLabel,
      formConfigurations: tableProps.labelFormConfigurations ?? null,
    },
  });

  // Compute column width ratios from saved preferences for visible columns
  const columnWidthRatios = useMemo(() => {
    type ColumnWidthsMap = Record<string, number | undefined>;
    type PrefsCustom = { custom?: { columnWidths?: ColumnWidthsMap } };
    const prefs = tableProps.preferenceDetails
      ?.preferences as unknown as PrefsCustom;
    const customWidths = prefs?.custom?.columnWidths as
      | ColumnWidthsMap
      | undefined;

    // Baseline width to prevent zero-width columns dominating ratios
    const BASELINE = 1;
    // Compute the average of saved widths (if any) to use as a fair fallback
    const savedValues = visibleColumns
      .map((col) => customWidths?.[col])
      .filter(
        (n): n is number => typeof n === 'number' && isFinite(n) && n > 0
      );
    const avgSaved =
      savedValues.length > 0
        ? savedValues.reduce((a, b) => a + b, 0) / savedValues.length
        : undefined;

    // Build widths list aligned to visible columns
    const widths = visibleColumns.map((col) => {
      const saved = customWidths?.[col];
      if (typeof saved === 'number' && isFinite(saved) && saved > 0) {
        return saved;
      }

      // Fall back to the field minWidth if available to better reflect UI proportions
      const fieldMin = (
        fields as unknown as Record<string, { minWidth?: number }>
      )[col]?.minWidth;
      if (typeof fieldMin === 'number' && isFinite(fieldMin) && fieldMin > 0) {
        return fieldMin;
      }

      // If we have any saved widths, use their average so missing columns aren't tiny
      if (typeof avgSaved === 'number' && isFinite(avgSaved) && avgSaved > 0) {
        return avgSaved;
      }

      // Final fallback to a small baseline weight
      return BASELINE;
    });

    // Optionally weight the last column to fine-tune perceived balance in PDF,
    // but only when explicit saved widths aren't defined for all visible columns.
    const allHaveSaved = visibleColumns.every((col) => {
      return (
        typeof customWidths?.[col] === 'number' &&
        isFinite(customWidths[col] as number) &&
        (customWidths[col] as number) > 0
      );
    });
    if (
      !allHaveSaved &&
      widths.length > 0 &&
      Number.isFinite(LAST_COLUMN_RATIO_WEIGHT) &&
      LAST_COLUMN_RATIO_WEIGHT > 0
    ) {
      const lastIdx = widths.length - 1;
      widths[lastIdx] =
        widths[lastIdx]! * (LAST_COLUMN_RATIO_WEIGHT - widths.length * 0.15);
    }

    const sum = widths.reduce((acc, n) => acc + (isFinite(n) ? n : 0), 0);
    if (sum <= 0) {
      return undefined;
    }

    return widths.map((w) => Number((w / sum).toFixed(4)));
  }, [tableProps.preferenceDetails?.preferences, visibleColumns, fields]);

  // Calculate ribbon card data for risk register template
  const ribbonCards = useMemo(() => {
    if (!ribbonFilters) {
      return [];
    }

    return ribbonFilters.map((filter) => {
      // Apply the filter to count matching items
      let matchingCount = 0;

      if (filter.itemFilterQuery) {
        const hasTokens = filter.itemFilterQuery.tokens?.length > 0;
        const hasTokenGroups =
          (filter.itemFilterQuery.tokenGroups?.length ?? 0) > 0;

        if (!hasTokens && !hasTokenGroups) {
          // Empty filter means "All" - count everything
          matchingCount = tableProps.allItems?.length || 0;
        } else {
          // Apply token filters to count matching items
          matchingCount = (tableProps.allItems || []).filter((item: T) => {
            try {
              return applyTokenFilter(
                item,
                filter.itemFilterQuery,
                tableProps.filteringProperties
              );
            } catch {
              // If filter fails, don't count the item
              return false;
            }
          }).length;
        }
      } else {
        // If no filter query, assume it's "All" and count everything
        matchingCount = tableProps.allItems?.length || 0;
      }

      // Determine if this ribbon filter matches the current table filter (highlighted in UI)
      const ribbonQuery = {
        tokens: [...(filter.itemFilterQuery.tokens ?? [])],
        tokenGroups: filter.itemFilterQuery.tokenGroups
          ? [...filter.itemFilterQuery.tokenGroups]
          : undefined,
        operation: filter.itemFilterQuery.operation,
      };
      const isHighlighted = isEqual(
        ribbonQuery,
        tableProps.propertyFilterQuery
      );

      return {
        title: filter.title,
        value: matchingCount,
        highlighted: isHighlighted,
      };
    });
  }, [
    ribbonFilters,
    tableProps.allItems,
    tableProps.filteringProperties,
    tableProps.propertyFilterQuery,
  ]);

  const downloadFromUrl = useCallback(
    async (downloadUrl: string, filename: string): Promise<void> => {
      try {
        // Plain fetch: a presigned S3 URL is query-string authorised and an
        // Authorization header would invalidate the signature.
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`Download failed with status ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        // Fallback: navigate directly; Content-Disposition on the presigned
        // URL enforces the filename.
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    []
  );

  type ExportOverrides = {
    titleOverride?: string;
    subtitleOverride?: string;
    hideRibbon?: boolean;
    orientation?: 'landscape' | 'portrait';
  };
  const exportToPdf = useCallback(
    async (overrides?: ExportOverrides): Promise<void> => {
      try {
        // Generate table data using the table hook
        const tableData = generateTableData();

        // Warn if too many columns may impact PDF layout
        const columnCount = Array.isArray(tableData.headers)
          ? tableData.headers.length
          : 0;
        const COLUMN_THRESHOLD = 10; // assumption: >10 columns may not format well

        const headers = tableData.headers.map((h: unknown) =>
          h === null || h === undefined ? '' : String(h)
        );
        const rows = tableData.rows.map((row: unknown[]) =>
          row.map((cell: unknown) =>
            cell === null || cell === undefined ? '' : String(cell)
          )
        );

        const register: TableData = {
          headers,
          rows,
          cellStyles: buildCellStyles(rows, tableData.cellStyles),
          columnWidthRatios,
        };

        const templateId = pdfTemplateId || 'default-register';
        const includeRibbon =
          !overrides?.hideRibbon &&
          ribbonFilters !== undefined &&
          ribbonCards.length > 0;

        const filtersSummary = buildFiltersSummary(
          tableData.metadata.filterInfo,
          tableData.metadata.appliedFilters
        );

        const payload: ReportPayload = {
          data: {
            register,
            ...(includeRibbon ? { ribbonCards } : {}),
            ...(filtersSummary ? { filtersSummary } : {}),
          },
        };

        const cleanLabel = entityLabel.toLowerCase().replace(/\s+/g, '-');
        const filename = `${cleanLabel}-${dayjs().format('YYYY-MM-DD-HH-mm-ss')}.pdf`;

        if (columnCount > COLUMN_THRESHOLD) {
          addNotification({
            type: 'success',
            content: (
              <span style={{ whiteSpace: 'pre-line' }}>
                {t(
                  'export.pdf_many_columns_warning',
                  `PDF export started.\n\nThis export includes ${columnCount} columns.\nConsider reducing visible columns for a better result.`
                )}
              </span>
            ),
          });
        } else {
          addNotification({
            type: 'success',
            content: t('export.pdf_export_started', 'PDF export started...'),
          });
        }

        const response = await axios.post<PdfGenerationResponse>(
          '/pdf/generate',
          {
            templateId,
            payload,
            options: {
              title: overrides?.titleOverride || entityLabel,
              subtitle:
                overrides?.subtitleOverride ||
                orgName ||
                dayjs().format('MMMM D, YYYY'),
              filename,
              orientation: overrides?.orientation,
            },
          }
        );

        if (response.data.success && response.data.downloadUrl) {
          await downloadFromUrl(
            response.data.downloadUrl,
            ensurePdfExtension(response.data.filename || filename)
          );
          addNotification({
            type: 'success',
            content: t('export.pdf_export_success', 'PDF export completed'),
          });
        } else {
          throw new Error('Failed to generate PDF');
        }
      } catch (e) {
        handleError(e);
        addNotification({
          type: 'error',
          content: t('export.pdf_export_failed', 'PDF export failed'),
        });
      }
    },
    [
      generateTableData,
      ribbonCards,
      entityLabel,
      pdfTemplateId,
      ribbonFilters,
      addNotification,
      t,
      axios,
      downloadFromUrl,
      columnWidthRatios,
      orgName,
    ]
  );

  const exportToPdfString = useCallback(() => {
    // For backwards compatibility, return CSV data as string
    return exportToCsvString();
  }, [exportToCsvString]);

  return {
    exportToPdf,
    exportToPdfString,
  };
}
